import fileStorage from './fileStorage';

// 种子数据（仅用于演示）
const seedPosts = [
  {
    id: '1000',
    title: '欢迎使用博客系统',
    content: '这是您的第一篇博客文章。您可以编辑或删除这篇文章。',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    tags: ['欢迎', '介绍'],
    status: 'published'
  }
];

// 认证状态（模拟）
let isAuthenticated = false;

// 读取文章数据
export const readPosts = async () => {
  try {
    const posts = await fileStorage.readAllPosts();
    
    // 如果没有文章，返回种子数据用于演示
    if (posts.length === 0) {
      console.log('No posts found in data folder, returning seed data');
      return seedPosts;
    }
    
    return posts;
  } catch (error) {
    console.error('Error reading posts:', error);
    return seedPosts;
  }
};

// 读取单个文章
export const getPost = async (postId) => {
  try {
    const post = await fileStorage.readPost(postId);
    if (post) {
      return post;
    }
    
    // 如果找不到文章，返回种子数据中的对应文章
    const seedPost = seedPosts.find(p => p.id === postId);
    return seedPost || null;
  } catch (error) {
    console.error('Error getting post:', error);
    return null;
  }
};

// 创建新文章（直接保存到用户选择的文件夹）
export const createPost = async (postData) => {
  try {
    const newPost = {
      id: Date.now().toString(),
      title: postData.title || '未命名文章',
      summary: postData.summary || '', // 添加摘要字段
      content: postData.content || '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      tags: postData.tags || [],
      status: 'published'
    };
    
    // 使用文件保存对话框让用户选择保存位置
    const fileName = fileStorage.generateFileName(newPost.title);
    const options = {
      suggestedName: fileName,
      types: [
        {
          description: 'JSON Files',
          accept: {
            'application/json': ['.json'],
          },
        },
      ],
    };
    
    const fileHandle = await window.showSaveFilePicker(options);
    const writable = await fileHandle.createWritable();
    await writable.write(JSON.stringify(newPost, null, 2));
    await writable.close();
    
    return {
      success: true,
      post: newPost,
      message: '文章创建成功并已保存到您选择的文件夹'
    };
  } catch (error) {
    console.error('Error creating post:', error);
    return {
      success: false,
      message: '创建文章失败：' + error.message
    };
  }
};

// 更新文章（直接保存到用户选择的文件夹）
export const updatePost = async (postId, postData) => {
  try {
    const existingPost = await getPost(postId);
    if (!existingPost) {
      return {
        success: false,
        message: '文章不存在'
      };
    }
    
    const updatedPost = {
      ...existingPost,
      ...postData,
      updatedAt: new Date().toISOString()
    };
    
    // 使用文件保存对话框让用户选择保存位置
    const fileName = fileStorage.generateFileName(updatedPost.title);
    const options = {
      suggestedName: fileName,
      types: [
        {
          description: 'JSON Files',
          accept: {
            'application/json': ['.json'],
          },
        },
      ],
    };
    
    const fileHandle = await window.showSaveFilePicker(options);
    const writable = await fileHandle.createWritable();
    await writable.write(JSON.stringify(updatedPost, null, 2));
    await writable.close();
    
    return {
      success: true,
      post: updatedPost,
      message: '文章更新成功并已保存到您选择的文件夹'
    };
  } catch (error) {
    console.error('Error updating post:', error);
    return {
      success: false,
      message: '更新文章失败：' + error.message
    };
  }
};

// 导出单篇文章
export const exportPost = async (postId) => {
  try {
    const post = await getPost(postId);
    if (!post) {
      return {
        success: false,
        message: '文章不存在'
      };
    }
    
    const success = await fileStorage.exportSinglePost(post);
    return {
      success: success,
      message: success ? '文章导出成功' : '文章导出失败'
    };
  } catch (error) {
    console.error('Error exporting post:', error);
    return {
      success: false,
      message: '导出文章失败'
    };
  }
};

// 导出所有文章
export const exportAllPosts = async () => {
  try {
    const success = await fileStorage.exportAllPosts();
    return {
      success: success,
      message: success ? '所有文章导出成功' : '导出失败'
    };
  } catch (error) {
    console.error('Error exporting all posts:', error);
    return {
      success: false,
      message: '导出失败'
    };
  }
};

// 导入文章（从JSON文件导入）
export const importPosts = async (file) => {
  try {
    if (!file) {
      return {
        success: false,
        message: '请选择要导入的文件'
      };
    }
    
    // 检查文件类型
    if (!file.name.endsWith('.json')) {
      return {
        success: false,
        message: '请选择JSON格式的文件'
      };
    }
    
    const content = await readFileContent(file);
    const posts = JSON.parse(content);
    
    if (Array.isArray(posts)) {
      // 验证文章数据格式
      const validPosts = posts.filter(post => 
        post && 
        typeof post === 'object' && 
        post.title && 
        post.content
      );
      
      if (validPosts.length === 0) {
        return {
          success: false,
          message: '文件中没有有效的文章数据'
        };
      }
      
      // 保存导入的文章到文件系统
      await saveImportedPosts(validPosts);
      
      return {
        success: true,
        posts: validPosts,
        message: `成功导入 ${validPosts.length} 篇文章`
      };
    }
    
    return {
      success: false,
      message: '导入的文件格式不正确，必须是JSON数组格式'
    };
  } catch (error) {
    console.error('Error importing posts:', error);
    return {
      success: false,
      message: `导入失败: ${error.message}`
    };
  }
};

// 读取文件内容
const readFileContent = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (e) => reject(new Error('文件读取失败'));
    reader.readAsText(file);
  });
};

// 保存导入的文章
const saveImportedPosts = async (posts) => {
  // 这里需要实现将文章保存到文件系统的逻辑
  // 由于当前系统使用文件系统存储，导入的文章需要手动保存
  console.log('导入的文章数据:', posts);
  return true;
};

// 归档文章（按月份分组显示所有文章）
export const archivePosts = async () => {
  try {
    const posts = await readPosts();
    
    // 按创建时间倒序排序
    const sortedPosts = posts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    
    // 按月份分组
    const groups = {};
    sortedPosts.forEach(post => {
      const date = new Date(post.createdAt);
      const monthKey = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
      const monthName = `${date.getFullYear()}年${date.getMonth() + 1}月`;
      
      if (!groups[monthKey]) {
        groups[monthKey] = {
          month: monthName,
          items: []
        };
      }
      
      groups[monthKey].items.push(post);
    });
    
    // 转换为数组并按月份倒序排序
    const result = Object.values(groups).sort((a, b) => {
      const aDate = new Date(a.items[0].createdAt);
      const bDate = new Date(b.items[0].createdAt);
      return bDate - aDate;
    });
    
    return result;
  } catch (error) {
    console.error('Error archiving posts:', error);
    return [];
  }
};

// 认证相关函数
export const isAuthed = () => {
  // 从localStorage读取登录状态，如果存在则返回true
  const storedAuth = localStorage.getItem('blog_auth');
  if (storedAuth) {
    try {
      const authData = JSON.parse(storedAuth);
      // 检查是否在有效期内（7天内有效）
      if (authData.timestamp && Date.now() - authData.timestamp < 7 * 24 * 60 * 60 * 1000) {
        isAuthenticated = true;
        return true;
      } else {
        // 登录状态已过期，清除存储
        localStorage.removeItem('blog_auth');
        isAuthenticated = false;
        return false;
      }
    } catch (error) {
      // 解析失败，清除无效数据
      localStorage.removeItem('blog_auth');
      isAuthenticated = false;
      return false;
    }
  }
  return isAuthenticated;
};

export const login = async (credentials) => {
  // 验证用户名和密码
  if (credentials.username === '惜哉剑气疏' && credentials.password === '123456') {
    isAuthenticated = true;
    
    // 将登录状态保存到localStorage，包含时间戳
    localStorage.setItem('blog_auth', JSON.stringify({
      username: credentials.username,
      timestamp: Date.now()
    }));
    
    return {
      success: true,
      message: '登录成功'
    };
  }
  
  // 检查是否为空
  if (!credentials.username || !credentials.password) {
    return {
      success: false,
      message: '用户名或密码不能为空'
    };
  }
  
  // 用户名或密码错误
  return {
    success: false,
    message: '用户名或密码错误'
  };
};

export const logout = () => {
  isAuthenticated = false;
  // 清除localStorage中的登录状态
  localStorage.removeItem('blog_auth');
  return {
    success: true,
    message: '已登出'
  };
};

// 文章列表（支持分页和搜索）
export const listPosts = async ({ page = 1, pageSize = 8, keyword = '' } = {}) => {
  try {
    const allPosts = await readPosts();
    
    // 过滤文章（根据关键词搜索标题、内容、标签）
    let filteredPosts = allPosts;
    if (keyword.trim()) {
      const searchTerm = keyword.toLowerCase();
      filteredPosts = allPosts.filter(post => 
        post.title?.toLowerCase().includes(searchTerm) ||
        post.summary?.toLowerCase().includes(searchTerm) ||  // 添加摘要搜索
        post.content?.toLowerCase().includes(searchTerm) ||
        (post.tags && post.tags.some(tag => tag.toLowerCase().includes(searchTerm)))
      );
    }
    
    // 按创建时间倒序排序
    filteredPosts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    
    // 计算分页
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedPosts = filteredPosts.slice(startIndex, endIndex);
    
    return {
      items: paginatedPosts.map(post => ({
        id: post.id,
        title: post.title,
        summary: post.summary || '', // 添加摘要字段
        content: post.content, // 添加内容字段用于搜索
        createdAt: post.createdAt,
        updatedAt: post.updatedAt,
        tags: post.tags,
        status: post.status
      })),
      total: filteredPosts.length,
      page: page,
      pageSize: pageSize,
      totalPages: Math.ceil(filteredPosts.length / pageSize)
    };
  } catch (error) {
    console.error('Error listing posts:', error);
    return {
      items: [],
      total: 0,
      page: page,
      pageSize: pageSize,
      totalPages: 0
    };
  }
};

// 初始化（检查是否有文章数据）
export const ensureInit = async () => {
  const posts = await readPosts();
  console.log(`Found ${posts.length} posts in data folder`);
  return posts.length > 0;
};